import React from 'react';
import PropTypes from 'prop-types';

import 'react-daterange-picker/dist/css/react-calendar.css';

import { useTranslation, withTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import DateRangePicker from 'react-daterange-picker';
import { Loader } from 'semantic-ui-react';
import ChartedEvent from '../../../../../components/chartedEvent';

import PopupFilter from './popupFilter';
import SelectFilter from './selectFilter';
import MultiSelectFilter from './multiSelectFilter';

import { QueryManager } from '@weezevent/nacre';

/**
 * Scan status filter
 */
export const ScanStatusFilter = props => {
    const { t } = useTranslation();
    return (
        <QueryManager>
            <SelectFilter name="scan_status" {...props} label={t('attendees.filters.scanStatus')} />
        </QueryManager>
    );
};

ScanStatusFilter.propTypes = {
    onClear: PropTypes.func,
    onChange: PropTypes.func,
    options: PropTypes.array,
    setClearer: PropTypes.func
};

/**
 * Ticket origin filter
 */
export const TicketOriginFilter = props => {
    const { t } = useTranslation();
    return (
        <QueryManager>
            <SelectFilter name="ticket_origin" {...props} label={t('attendees.filters.ticketOrigin')} />
        </QueryManager>
    );
};

TicketOriginFilter.propTypes = {
    onClear: PropTypes.func,
    onChange: PropTypes.func,
    options: PropTypes.array,
    setClearer: PropTypes.func
};

/**
 * Ticket type filter
 */
export const TicketTypeFilter = props => {
    const { t } = useTranslation();
    return (
        <QueryManager>
            <SelectFilter name="ticket_type" {...props} label={t('attendees.filters.ticketType')} />
        </QueryManager>
    );
};

TicketTypeFilter.propTypes = {
    onClear: PropTypes.func,
    onChange: PropTypes.func,
    options: PropTypes.array,
    setClearer: PropTypes.func
};

/**
 * Zone filter
 */
export function ZoneFilter({ zones, event, onChange = () => {}, onClear = () => {}, setClearer }) {
    const { t } = useTranslation();
    const name = 'booking__seat__zone__in';

    const idsToZones = React.useCallback(
        value => {
            return zones.filter(zone => value.includes(zone.id()));
        },
        [zones]
    );

    const getLabel = React.useCallback(
        value => {
            let zones = idsToZones(value);
            return zones.map(zone => zone.name()).join(' - ');
        },
        [zones, idsToZones]
    );

    if (!zones) {
        return <Loader />;
    }

    let options = zones.map(zone => ({
        text: zone.name(),
        value: zone.id()
    }));

    return (
        <ChartedEvent event={event}>
            <QueryManager>
                <MultiSelectFilter
                    onChange={onChange}
                    onClear={onClear}
                    setClearer={setClearer}
                    name={name}
                    filterLabel={t('attendees.filters.zone')}
                    options={options}
                    getLabel={getLabel}
                />
            </QueryManager>
        </ChartedEvent>
    );
}

ZoneFilter.propTypes = {
    event: PropTypes.object.isRequired,
    onClear: PropTypes.func,
    onChange: PropTypes.func,
    zones: PropTypes.array,
    setClearer: PropTypes.func
};

/**
 * Rate filter
 */
export const RateFilter = ({ onClear, onChange, setClearer, rates, event }) => {
    const { t } = useTranslation();
    if (!rates || !rates.length) {
        return <Loader />;
    }

    let options = rates.map(rate => ({
        text: rate.resolved_name,
        value: rate.id
    }));

    function getLabel(selectedRates) {
        return rates
            .filter(rate => selectedRates.includes(rate.id))
            .map(rate => rate.resolved_name)
            .join(' - ');
    }

    return (
        <QueryManager>
            <MultiSelectFilter
                onChange={onChange}
                onClear={onClear}
                setClearer={setClearer}
                width="200px"
                name={event.is_seated ? 'booking__rate__in' : 'rate__in'}
                filterLabel={t('attendees.filters.rate')}
                options={options}
                getLabel={value => getLabel(value)}
            />
        </QueryManager>
    );
};

RateFilter.propTypes = {
    onClear: PropTypes.func,
    onChange: PropTypes.func,
    rates: PropTypes.array,
    setClearer: PropTypes.func,
    event: PropTypes.object.isRequired
};

/**
 * Partner filter
 */
export const PartnerFilter = ({ onClear, onChange, setClearer, partners }) => {
    const { t } = useTranslation();
    if (!partners || !partners.length) {
        return <Loader />;
    }

    let options = partners.map(partner => ({
        text: partner.name,
        value: partner.uuid
    }));

    function getLabel(selectedPartners) {
        return partners
            .filter(partner => selectedPartners.includes(partner.uuid))
            .map(partner => partner.name)
            .join(' - ');
    }

    return (
        <QueryManager>
            <MultiSelectFilter
                onChange={onChange}
                onClear={onClear}
                setClearer={setClearer}
                width="200px"
                name={'partner_id'}
                filterLabel={t('attendees.filters.partner')}
                options={options}
                getLabel={value => getLabel(value)}
            />
        </QueryManager>
    );
};

PartnerFilter.propTypes = {
    onClear: PropTypes.func,
    onChange: PropTypes.func,
    partners: PropTypes.array,
    setClearer: PropTypes.func
};

/**
 * Inventory filter
 */
export const InventoryFilter = ({ event, onClear, onChange, setClearer, inventories }) => {
    const { t } = useTranslation();
    if (!inventories || !inventories.length) {
        return <Loader />;
    }

    let options = inventories.map(inventory => ({
        text: inventory.name,
        value: inventory.uuid
    }));

    function getLabel(selectedInventories) {
        return inventories
            .filter(inventory => selectedInventories.includes(inventory.uuid))
            .map(inventory => inventory.name)
            .join(' - ');
    }

    return (
        <ChartedEvent event={event}>
            <QueryManager>
                <MultiSelectFilter
                    onChange={onChange}
                    onClear={onClear}
                    setClearer={setClearer}
                    width="200px"
                    name="booking__inventory__in"
                    filterLabel={t('attendees.filters.inventory')}
                    options={options}
                    getLabel={value => getLabel(value)}
                />
            </QueryManager>
        </ChartedEvent>
    );
};

InventoryFilter.propTypes = {
    event: PropTypes.object.isRequired,
    onClear: PropTypes.func,
    onChange: PropTypes.func,
    inventories: PropTypes.array,
    setClearer: PropTypes.func
};

/*
 * Returns a button which allows date range selection
 */
class DateFilter extends React.Component {
    constructor(props) {
        super(props);

        let value = null;
        let query = this.props.query();

        let start = query[DateFilter.START_NAME];
        let end = query[DateFilter.END_NAME];

        if (start || end) {
            value = {};
            value.start = start ? moment(start) : null;
            value.end = end ? moment(end) : null;
        }

        this.state = { value };

        if (this.props.setClearer) {
            this.props.setClearer(() => this.clear(false));
        }
    }

    getDateRange() {
        if (!this.state.value) {
            return null;
        }

        let { start, end } = this.state.value;
        let leftpad = what => `00${what}`.slice(-2);
        let django = date => `${date.getFullYear()}-${leftpad(date.getMonth() + 1)}-${leftpad(date.getDate())}`;
        return [django(start.toDate()), django(end.toDate())];
    }

    /**
     * Handle range change
     */
    onChange(range) {
        this.setState(
            {
                value: range
            },
            () => {
                let [start, end] = this.getDateRange();
                let filters = {
                    [DateFilter.END_NAME]: end,
                    [DateFilter.START_NAME]: start
                };

                this.props.addToQuery(DateFilter.END_NAME, end);
                this.props.addToQuery(DateFilter.START_NAME, start);

                this.props.onChange(filters);
            }
        );
    }

    clear(triggerEvents = true) {
        this.setState(
            {
                value: null
            },
            () => {
                let filters = {
                    [DateFilter.END_NAME]: undefined,
                    [DateFilter.START_NAME]: undefined
                };

                this.props.removeFromQuery(DateFilter.END_NAME);
                this.props.removeFromQuery(DateFilter.START_NAME);

                if (triggerEvents) {
                    this.props.onClear(filters);
                    this.props.onChange(filters);
                }
            }
        );
    }

    render() {
        let buttonProps = {};
        if (this.state.value) {
            const format = 'DD/MM/YYYY';
            let { start, end } = this.state.value;
            buttonProps.label = `${start.format(format)} - ${end.format(format)}`;
            buttonProps.basic = false;
        }

        const { t } = this.props;

        return (
            <PopupFilter
                wide
                label={this.props.date ? 'DATE' : t('attendees.filters.date')}
                className="popover-calendar"
                buttonProps={buttonProps}
                value={this.state.value}
                clear={this.clear.bind(this)}
            >
                {/*Comment purposely left here so we remember to get event sales_start_date */}
                {/*minimumDate={this.props.event.startDate()}*/}
                <DateRangePicker
                    firstOfWeek={1}
                    numberOfCalendars={2}
                    selectionType="range"
                    onSelect={date => this.onChange(date)}
                    value={this.state.value}
                    singleDateRange={true}
                />
            </PopupFilter>
        );
    }
}

DateFilter.START_NAME = 'order__sold_date_after';
DateFilter.END_NAME = 'order__sold_date_before';

DateFilter.defaultProps = {
    onClear: () => {},
    onChange: () => {},
    date: null,
    query: () => ({}),
    addToQuery: () => {},
    removeFromQuery: () => {}
};

DateFilter.propTypes = {
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    date: PropTypes.number,
    event: PropTypes.object,
    setClearer: PropTypes.func,
    query: PropTypes.func,
    addToQuery: PropTypes.func,
    removeFromQuery: PropTypes.func,
    t: PropTypes.func.isRequired
};

export default withTranslation()(DateFilter);
